import type { TypeSelectOption } from '@thefarmersfront/kitchen';

import { EXTERNAL_URLS } from '~/constants/externalUrls';

/**
 * [배송대행 관리] 페이지네이션 관련 상수
 */
export const INITIAL_DELIVERY_AGENCY_PAGE_SIZE = 50;
export const INITIAL_DELIVERY_AGENCY_PAGE_CURSOR = 1;

export const DELIVERY_AGENCY_PAGE_SIZE = {
  50: {
    value: 50,
    text: '50개',
  },
  100: {
    value: 100,
    text: '100개',
  },
  200: {
    value: 200,
    text: '200개',
  },
} as const;

export type TypeClaimPageSize = keyof typeof DELIVERY_AGENCY_PAGE_SIZE;

/**
 * [배송대행 관리][운송장 관리] 페이지네이션 사이즈 옵션 (운송장 관리 전용)
 */
export const DELIVERY_AGENCY_TRACKING_PAGE_SIZE = {
  ...DELIVERY_AGENCY_PAGE_SIZE,
  300: {
    value: 300,
    text: '300개',
  },
  500: {
    value: 500,
    text: '500개',
  },
} as const;

/**
 * [배송대행 관리] 날짜 검색 최대 기간
 */
export const MAX_DELIVERY_AGENCY_DATE_RANGE = 31;

/**
 * [배송대행 관리] 상품명 검색 키워드 타입 코드
 * 상품명 키워드 선택 시 배송 예정일을 필수로 강제하기 위한 분기 기준값
 */
export const DELIVERY_AGENCY_PRODUCT_NAME_KEYWORD_TYPE = 'PRODUCT_NAME';

/**
 * [배송대행 관리] 툴팁 안내 문구
 */
export const DELIVERY_AGENCY_TOOLTIP_MESSAGE = {
  SEND_ALIM_TALK_IMMEDIATELY:
    "해당 주문의 알림톡 즉시 발송 여부와 상관 없이\n출고번호가 같은 다른 주문이 알림톡 즉시 발송 'Y' 로 설정되어 있으면 배송완료 직후 알림톡이 발송됩니다.",
} as const;

/**
 * [배송대행 관리] 주문 상태
 * TODO - 출고완료 추후에 추가 (오픈스펙 아님)
 */
export const DELIVERY_AGENCY_ORDER_STATUS = {
  OUTBOUND_READY: 'OUTBOUND_READY',
  OUTBOUND_PROCESSING: 'OUTBOUND_PROCESSING',
  ON_THE_WAY: 'ON_THE_WAY',
  DELIVERY_COMPLETED: 'DELIVERY_COMPLETED',
  CANCELED: 'CANCELED',
  DELIVERY_FAILED: 'DELIVERY_FAILED',
} as const;

export type TypeDeliveryAgencyOrderStatus =
  (typeof DELIVERY_AGENCY_ORDER_STATUS)[keyof typeof DELIVERY_AGENCY_ORDER_STATUS];

/**
 * [배송대행 관리] 주문 상태 뱃지 색상 매핑
 */
export const DELIVERY_AGENCY_ORDER_STATUS_BADGE_COLOR = {
  [DELIVERY_AGENCY_ORDER_STATUS.OUTBOUND_READY]: 'secondary',
  [DELIVERY_AGENCY_ORDER_STATUS.OUTBOUND_PROCESSING]: 'info',
  [DELIVERY_AGENCY_ORDER_STATUS.ON_THE_WAY]: 'info',
  [DELIVERY_AGENCY_ORDER_STATUS.DELIVERY_COMPLETED]: 'success',
  [DELIVERY_AGENCY_ORDER_STATUS.CANCELED]: 'error',
  [DELIVERY_AGENCY_ORDER_STATUS.DELIVERY_FAILED]: 'warning',
} as const;

/**
 * [배송대행 관리] 운송장 상태
 */
export const DELIVERY_AGENCY_SHIPPING_LABEL_STATUS = {
  OUTBOUND_READY: 'OUTBOUND_READY',
  OUTBOUND_PROCESSING: 'OUTBOUND_PROCESSING',
  ON_THE_WAY: 'ON_THE_WAY',
  DELIVERY_COMPLETED: 'DELIVERY_COMPLETED',
  DELIVERY_CANCELED: 'DELIVERY_CANCELED',
  DELIVERY_FAILED: 'DELIVERY_FAILED',
} as const;

export type TypeDeliveryAgencyShippingLabelStatus =
  (typeof DELIVERY_AGENCY_SHIPPING_LABEL_STATUS)[keyof typeof DELIVERY_AGENCY_SHIPPING_LABEL_STATUS];

/**
 * [배송대행 관리] 운송장 상태 뱃지 색상 매핑
 */
export const DELIVERY_AGENCY_SHIPPING_LABEL_STATUS_BADGE_COLOR = {
  [DELIVERY_AGENCY_SHIPPING_LABEL_STATUS.OUTBOUND_READY]: 'secondary',
  [DELIVERY_AGENCY_SHIPPING_LABEL_STATUS.OUTBOUND_PROCESSING]: 'info',
  [DELIVERY_AGENCY_SHIPPING_LABEL_STATUS.ON_THE_WAY]: 'info',
  [DELIVERY_AGENCY_SHIPPING_LABEL_STATUS.DELIVERY_COMPLETED]: 'success',
  [DELIVERY_AGENCY_SHIPPING_LABEL_STATUS.DELIVERY_CANCELED]: 'error',
  [DELIVERY_AGENCY_SHIPPING_LABEL_STATUS.DELIVERY_FAILED]: 'warning',
} as const;

/**
 * [배송대행관리] 배송사 코드 매핑
 */
export const DELIVERY_AGENCY_COURIER_CODE = {
  CJT: {
    value: 'CJT',
    text: 'CJ',
  },
  NM: {
    value: 'NM',
    text: '넥스트마일',
  },
  LTT: {
    value: 'LTT',
    text: '롯데',
  },
} as const;

export const DELIVERY_AGENCY_TRACKING_LINKS = {
  [DELIVERY_AGENCY_COURIER_CODE.CJT.value]: EXTERNAL_URLS.DELIVERY_AGENCY_TRACKING_LINK_CJT,
  [DELIVERY_AGENCY_COURIER_CODE.NM.value]: EXTERNAL_URLS.DELIVERY_AGENCY_TRACKING_LINK_KURLY_NEXT_MILE,
  [DELIVERY_AGENCY_COURIER_CODE.LTT.value]: EXTERNAL_URLS.DELIVERY_AGENCY_TRACKING_LINK_LTT,
} as const;

export type TypeDeliveryAgencyCourierCode = keyof typeof DELIVERY_AGENCY_COURIER_CODE;

/**
 * [배송대행관리][운송장관리] 운송장 출력
 */
export const DELIVERY_AGENCY_TRACKING_PRINT_STATUS = {
  Y: { value: 'Y', text: '출력함' },
  N: { value: 'N', text: '출력안함' },
} as const;

export type TypeDeliveryAgencyTrackingPrintStatus =
  (typeof DELIVERY_AGENCY_TRACKING_PRINT_STATUS)[keyof typeof DELIVERY_AGENCY_TRACKING_PRINT_STATUS]['value'];

/**
 * [운송장 관리] 추가 운송장 1회 최대 생성 수량
 */
export const MAX_SHIPPING_LABEL_QUANTITY = 30;

/**
 * [배송 결과 리포트] 배송 유형 코드
 */
export const DELIVERY_AGENCY_DELIVERY_TYPE = {
  DAWN: 'DAWN',
  DAY: 'DAY',
} as const;

/**
 * [배송 결과 리포트] 화주사 노출 운송장 상태
 */
export const DELIVERY_AGENCY_REPORTED_SHIPPING_LABEL_STATUS = {
  OUTBOUND_READY_OR_PROCESSING: 'OUTBOUND_READY_OR_PROCESSING',
  ON_THE_WAY: 'ON_THE_WAY',
  DELIVERY_COMPLETED: 'DELIVERY_COMPLETED',
  DELIVERY_CANCELED: 'DELIVERY_CANCELED',
  DELIVERY_FAILED: 'DELIVERY_FAILED',
} as const;

/**
 * [배송 결과 리포트] 서머리 '진행 중' 카드 구성 상태 코드
 */
export const DELIVERY_AGENCY_REPORT_IN_PROGRESS_STATUS_CODES = [
  DELIVERY_AGENCY_REPORTED_SHIPPING_LABEL_STATUS.OUTBOUND_READY_OR_PROCESSING,
  DELIVERY_AGENCY_REPORTED_SHIPPING_LABEL_STATUS.ON_THE_WAY,
] as const;

/**
 * [배송 결과 리포트] 화주사 노출 운송장 상태 뱃지 색상 매핑
 */
export const DELIVERY_AGENCY_REPORTED_SHIPPING_LABEL_STATUS_BADGE_COLOR = {
  [DELIVERY_AGENCY_REPORTED_SHIPPING_LABEL_STATUS.OUTBOUND_READY_OR_PROCESSING]: 'info',
  [DELIVERY_AGENCY_REPORTED_SHIPPING_LABEL_STATUS.ON_THE_WAY]: 'info',
  [DELIVERY_AGENCY_REPORTED_SHIPPING_LABEL_STATUS.DELIVERY_COMPLETED]: 'success',
  [DELIVERY_AGENCY_REPORTED_SHIPPING_LABEL_STATUS.DELIVERY_CANCELED]: 'secondary',
  [DELIVERY_AGENCY_REPORTED_SHIPPING_LABEL_STATUS.DELIVERY_FAILED]: 'warning',
} as const;

/**
 * [배송 결과 리포트] 화주사 노출 배송 결과
 */
export const DELIVERY_AGENCY_REPORTED_DELIVERY_RESULT = {
  NORMAL: { value: 'NORMAL', text: '정상배송' },
  DELAYED: { value: 'DELAYED', text: '지연배송' },
  NON_DELIVERED: { value: 'NON_DELIVERED', text: '미배송' },
  ALTERNATIVE_DELIVERY: { value: 'ALTERNATIVE_DELIVERY', text: '대응배송' },
  CONVERTED_DELIVERY: { value: 'CONVERTED_DELIVERY', text: '전환배송' },
} as const;

/**
 * [배송 결과 리포트] 화주사 노출 배송 결과 뱃지 색상 매핑
 */
export const DELIVERY_AGENCY_REPORTED_DELIVERY_RESULT_BADGE_COLOR = {
  [DELIVERY_AGENCY_REPORTED_DELIVERY_RESULT.NORMAL.value]: 'secondary',
  [DELIVERY_AGENCY_REPORTED_DELIVERY_RESULT.ALTERNATIVE_DELIVERY.value]: 'secondary',
  [DELIVERY_AGENCY_REPORTED_DELIVERY_RESULT.DELAYED.value]: 'warning',
  [DELIVERY_AGENCY_REPORTED_DELIVERY_RESULT.CONVERTED_DELIVERY.value]: 'warning',
  [DELIVERY_AGENCY_REPORTED_DELIVERY_RESULT.NON_DELIVERED.value]: 'error',
} as const;

export const DELIVERY_AGENCY_SORT_BY = {
  CREATED_AT_DESC: 'CREATED_AT_DESC',
  CREATED_AT_ASC: 'CREATED_AT_ASC',
} as const;

export type TypeDeliveryAgencySortBy = (typeof DELIVERY_AGENCY_SORT_BY)[keyof typeof DELIVERY_AGENCY_SORT_BY];

export const DELIVERY_AGENCY_ORDER_SORT_OPTIONS: TypeSelectOption<TypeDeliveryAgencySortBy>[] = [
  { value: DELIVERY_AGENCY_SORT_BY.CREATED_AT_DESC, text: '등록일 (내림차순)' },
  { value: DELIVERY_AGENCY_SORT_BY.CREATED_AT_ASC, text: '등록일 (오름차순)' },
];

export const DELIVERY_AGENCY_CREATED_SORT_OPTIONS: TypeSelectOption<TypeDeliveryAgencySortBy>[] = [
  { value: DELIVERY_AGENCY_SORT_BY.CREATED_AT_DESC, text: '생성일 (내림차순)' },
  { value: DELIVERY_AGENCY_SORT_BY.CREATED_AT_ASC, text: '생성일 (오름차순)' },
];

/**
 * [배송 주문 관리][주문 상세] 수령장소 코드
 */
export const DELIVERY_AGENCY_DELIVERY_LOCATION = {
  DOORSTEP: { value: 'DOORSTEP', text: '문 앞' },
  DELIVERY_BOX: { value: 'DELIVERY_BOX', text: '택배 수령실' },
  PUBLIC_DOOR: { value: 'PUBLIC_DOOR', text: '공동현관(대문) 앞' },
  ETC: { value: 'ETC', text: '기타' },
} as const;

export type TypeDeliveryAgencyDeliveryLocation =
  (typeof DELIVERY_AGENCY_DELIVERY_LOCATION)[keyof typeof DELIVERY_AGENCY_DELIVERY_LOCATION]['value'];

export const DELIVERY_AGENCY_DELIVERY_LOCATION_OPTIONS: TypeSelectOption<TypeDeliveryAgencyDeliveryLocation>[] =
  Object.values(DELIVERY_AGENCY_DELIVERY_LOCATION);

/** 수령장소 상세가 조건부 필수가 되는 수령장소 코드 (택배 수령실 / 기타) */
export const DELIVERY_AGENCY_DELIVERY_LOCATION_DETAIL_REQUIRED_CODES: TypeDeliveryAgencyDeliveryLocation[] = [
  DELIVERY_AGENCY_DELIVERY_LOCATION.DELIVERY_BOX.value,
  DELIVERY_AGENCY_DELIVERY_LOCATION.ETC.value,
];

/**
 * [배송 주문 관리][주문 상세] 출입방법 코드
 */
export const DELIVERY_AGENCY_ENTRANCE_METHOD = {
  PUBLIC_DOOR_PWD: { value: 'PUBLIC_DOOR_PWD', text: '공동현관 비밀번호' },
  CALL_SECURITY: { value: 'CALL_SECURITY', text: '경비실 호출' },
  FREE_ACCESS: { value: 'FREE_ACCESS', text: '자유출입' },
  ETC: { value: 'ETC', text: '기타' },
} as const;

export type TypeDeliveryAgencyEntranceMethod =
  (typeof DELIVERY_AGENCY_ENTRANCE_METHOD)[keyof typeof DELIVERY_AGENCY_ENTRANCE_METHOD]['value'];

export const DELIVERY_AGENCY_ENTRANCE_METHOD_OPTIONS: TypeSelectOption<TypeDeliveryAgencyEntranceMethod>[] =
  Object.values(DELIVERY_AGENCY_ENTRANCE_METHOD);

/** 출입방법 상세가 조건부 필수가 되는 출입방법 코드 (공동현관 비밀번호 / 기타) */
export const DELIVERY_AGENCY_ENTRANCE_METHOD_DETAIL_REQUIRED_CODES: TypeDeliveryAgencyEntranceMethod[] = [
  DELIVERY_AGENCY_ENTRANCE_METHOD.PUBLIC_DOOR_PWD.value,
  DELIVERY_AGENCY_ENTRANCE_METHOD.ETC.value,
];

/** 수령장소 상세 / 출입방법 상세 최대 입력 길이 */
export const DELIVERY_AGENCY_LOCATION_DETAIL_MAX_LENGTH = 100;
